import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { LinkTabNavHorizontal } from '../../components';
import { features } from '../../features/config'; // feature flags
// @feature:favourites
// @feature:favourites:end

import css from './UserNav.module.css';

/**
 * A component that renders a navigation bar for a user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const manageListingsTabMaybe = showManageListingsLink
    ? [
        {
          text: <FormattedMessage id="UserNav.yourListings" />,
          selected: currentPage === 'ManageListingsPage',
          linkProps: {
            name: 'ManageListingsPage',
          },
        },
      ]
    : [];

  // @feature:favourites
  const favouriteListingsTabMaybe = features.favourites
    ? [
        {
          text: <FormattedMessage id="UserNav.favouriteListings" />,
          selected: currentPage === 'FavouriteListingsPage',
          linkProps: {
            name: 'FavouriteListingsPage',
          },
        },
      ]
    : [];
  // @feature:favourites:end
  // @feature:profileAddresses
  const profileAddressesTabMaybe = features.profileAddresses
    ? [{
        text: <FormattedMessage id="UserNav.profileAddresses" />,
        selected: currentPage === 'ProfileAddressesPage',
        linkProps: { name: 'ProfileAddressesPage' },
      }]
    : [];
  // @feature:profileAddresses:end
  // @feature:stripeSubscriptions
  const subscriptionsTabMaybe = features.stripeSubscriptions
    ? [{
        text: <FormattedMessage id="UserNav.subscriptions" />,
        selected: currentPage === 'SubscriptionsPage',
        linkProps: { name: 'SubscriptionsPage' },
      }]
    : [];
  // @feature:stripeSubscriptions:end
  // @feature:notificationPreferences
  const notificationPreferencesTabMaybe = features.notificationPreferences
    ? [{
        text: <FormattedMessage id="UserNav.notificationPreferences" />,
        selected: currentPage === 'NotificationPreferencesPage',
        linkProps: { name: 'NotificationPreferencesPage' },
      }]
    : [];
  // @feature:notificationPreferences:end
  // @feature:vacationMode
  const vacationModeTabMaybe = features.vacationMode
    ? [{
        text: <FormattedMessage id="UserNav.vacationMode" />,
        selected: currentPage === 'VacationModePage',
        linkProps: { name: 'VacationModePage' },
      }]
    : [];
  // @feature:vacationMode:end

  const tabs = [
    // @feature:favourites
    ...favouriteListingsTabMaybe,
    // @feature:favourites:end
    // @feature:profileAddresses
    ...profileAddressesTabMaybe,
    // @feature:profileAddresses:end
    // @feature:stripeSubscriptions
    ...subscriptionsTabMaybe,
    // @feature:stripeSubscriptions:end
    // @feature:notificationPreferences
    ...notificationPreferencesTabMaybe,
    // @feature:notificationPreferences:end
    // @feature:vacationMode
    ...vacationModeTabMaybe,
    // @feature:vacationMode:end
    ...manageListingsTabMaybe,
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
  ];

  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={css.tab}
      tabs={tabs}
      skin="dark"
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

export default UserNav;
